/* Trades tab + all trade-management components (positions table, manage
   dialog with partial-close + move-SL-to-BE, confirm view, CSV download).
   Extracted from Dashboard.jsx during the 2026-02-14d refactor to keep the
   main page under 1,000 lines. */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { trades, channels } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";
import { Badge, cn } from "@/components/atoms";
import TradeDetailDialog from "@/pages/trades/TradeDetailDialog";
import { ChannelChip } from "@/pages/channels/ChannelChip";
import {
  ChartLineUp, ListBullets, ArrowUpRight, ArrowDownRight,
} from "@phosphor-icons/react";

/* ---- helpers ---- */

export function statusVariant(s) {
  if (s?.includes("waiting")) return "warn";
  if (s === "error") return "loss";
  if (s?.includes("provider_sl_updated")) return "info";
  if (s?.includes("closed")) return "muted";
  return "profit";
}
export function humanStatus(s) {
  return (s || "").replace(/_/g, " ");
}

/* Prices/quantities can carry full float precision from exact exchange fill
   data (e.g. reconciled trades) - round to 6 decimals and drop trailing
   zeros rather than rendering the raw value verbatim. */
export function formatNum(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return parseFloat(n.toFixed(6)).toString();
}

/* ---- Positions table (filterable by channel) ---- */

const OV_CLOSED = ["closed_by_signal", "closed_by_tp", "closed_by_sl", "closed_by_auto_roi", "error"];

export function PositionsTable({ trades: rows, emptyLabel = "No open positions yet. Inject a signal above to start.", mode = "live" }) {
  const chQ = useQuery({
    queryKey: ["channels"],
    queryFn: async () => (await channels.list()).data,
    staleTime: 10_000,
  });
  const chById = React.useMemo(() => {
    const m = {};
    for (const c of chQ.data || []) m[c.id] = c;
    return m;
  }, [chQ.data]);

  const uniqueChannelIds = React.useMemo(() => {
    const s = new Set();
    for (const t of rows) if (t.channel_id) s.add(t.channel_id);
    return Array.from(s);
  }, [rows]);
  const showChannelChip = uniqueChannelIds.length > 0;
  const showFilter = uniqueChannelIds.length > 1;

  const [filter, setFilter] = useState("all");
  const filtered = React.useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((t) => t.channel_id === filter);
  }, [rows, filter]);

  if (!rows.length) {
    return (
      <div className="py-16 text-center text-slate-500 text-sm">
        <ChartLineUp size={40} className="mx-auto mb-3 opacity-40" />
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {showFilter && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="positions-filter-bar">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1">Filter</span>
          <button
            className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
              filter === "all" ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
            onClick={() => setFilter("all")}
            data-testid="filter-all"
          >
            All ({rows.length})
          </button>
          {uniqueChannelIds.map((cid) => {
            const c = chById[cid];
            const label = c ? `${c.name} · ${c.exchange}` : `#${cid.slice(0, 8)}`;
            const count = rows.filter((t) => t.channel_id === cid).length;
            return (
              <button
                key={cid}
                className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
                  filter === cid ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                                 : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
                onClick={() => setFilter(cid)}
                data-testid={`filter-channel-${cid}`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>
      )}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 text-[11px] uppercase tracking-widest border-b border-slate-200">
              <th className="text-left py-3 px-2">Symbol</th>
              <th className="text-left py-3 px-2">Side</th>
              {showChannelChip && <th className="text-left py-3 px-2">Channel</th>}
              <th className="text-right py-3 px-2">Entry</th>
              {mode === "history" ? (
                <>
                  <th className="text-right py-3 px-2">Exit</th>
                  <th className="text-right py-3 px-2">Lev</th>
                  <th className="text-right py-3 px-2">PnL</th>
                  <th className="text-left py-3 px-2">Closed</th>
                </>
              ) : (
                <>
                  <th className="text-right py-3 px-2">Qty</th>
                  <th className="text-right py-3 px-2">Lev</th>
                  <th className="text-right py-3 px-2">SL</th>
                  <th className="text-right py-3 px-2">TP</th>
                  <th className="text-left py-3 px-2">SL Source</th>
                </>
              )}
              <th className="text-left py-3 px-2">Status</th>
              {mode === "live" && <th className="text-right py-3 px-2">uPnL</th>}
              <th className="py-3 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} data-testid={`position-row-${t.id}`} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="py-3 px-2 font-medium">
                  <div className="flex items-center gap-1.5">
                    {t.symbol}
                    {t.paper_mode && (
                      <Badge variant="warn" title="Simulated trade — no real order was placed on the exchange">
                        Paper
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="py-3 px-2">
                  <Badge variant={t.side === "long" ? "profit" : "loss"}>
                    {t.side === "long" ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {t.side.toUpperCase()}
                  </Badge>
                </td>
                {showChannelChip && (
                  <td className="py-3 px-2">
                    <ChannelChip channel={chById[t.channel_id]} rawId={t.channel_id} />
                  </td>
                )}
                <td className="py-3 px-2 mono text-right">{formatNum(t.entry_price)}</td>
                {mode === "history" ? (
                  <>
                    <td className="py-3 px-2 mono text-right">{OV_CLOSED.includes(t.status) ? formatNum(t.close_price) : "—"}</td>
                    <td className="py-3 px-2 mono text-right">{t.leverage}x</td>
                    <td className={cn(
                      "py-3 px-2 mono text-right font-semibold",
                      OV_CLOSED.includes(t.status)
                        ? ((t.realized_pnl || 0) > 0 ? "text-emerald-600" : (t.realized_pnl || 0) < 0 ? "text-red-600" : "")
                        : "text-slate-400 font-normal",
                    )}>
                      {OV_CLOSED.includes(t.status)
                        ? `${t.realized_pnl >= 0 ? "+" : "-"}$${Math.abs(t.realized_pnl || 0).toFixed(2)}`
                        : `${(t.unrealized_pnl || 0) >= 0 ? "+" : ""}${(t.unrealized_pnl || 0).toFixed(2)} unrl.`}
                    </td>
                    <td className="py-3 px-2 text-slate-500">{t.closed_at ? new Date(t.closed_at).toLocaleString() : "—"}</td>
                  </>
                ) : (
                  <>
                    <td className="py-3 px-2 mono text-right">{formatNum(t.quantity)}</td>
                    <td className="py-3 px-2 mono text-right">
                      {t.leverage}x
                      {t.signal_leverage && t.signal_leverage !== t.leverage && (
                        <span className="text-[10px] text-amber-600 ml-1" title={`Provider said ${t.signal_leverage}x`}>
                          (was {t.signal_leverage}x)
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2 mono text-right text-red-600">{formatNum(t.stop_loss)}</td>
                    <td className="py-3 px-2 mono text-right text-emerald-600">{formatNum(t.take_profit)}</td>
                    <td className="py-3 px-2">
                      <Badge variant={t.sl_source === "provider" ? "info" : "warn"}>
                        {t.sl_source}
                      </Badge>
                    </td>
                  </>
                )}
                <td className="py-3 px-2">
                  <Badge variant={statusVariant(t.status)}>{humanStatus(t.status)}</Badge>
                </td>
                {mode === "live" && (
                  <td className={cn(
                    "py-3 px-2 mono text-right",
                    (t.unrealized_pnl || 0) > 0 && "text-emerald-600",
                    (t.unrealized_pnl || 0) < 0 && "text-red-600",
                  )}>
                    {(t.unrealized_pnl || 0).toFixed(2)}
                  </td>
                )}
                <td className="py-3 px-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <TradeDetailDialog tradeId={t.id} />
                    <ManageTradeDialog trade={t} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- Manage trade dialog (partial close + SL to breakeven) ---- */

function ManageTradeDialog({ trade: t }) {
  const isClosed = ["closed_by_signal", "closed_by_tp", "closed_by_sl", "closed_by_auto_roi", "error"].includes(t.status);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const closeMut = useMutation({
    mutationFn: ({ id, partial_percent }) => trades.close(id, partial_percent),
    onSuccess: (_res, vars) => {
      const label = vars.partial_percent && vars.partial_percent < 100
        ? `Closed ${vars.partial_percent}% of ${t.symbol}`
        : `Closed ${t.symbol}`;
      toast.success(label);
      qc.invalidateQueries();
      setConfirm(null);
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Close failed"),
  });

  const beMut = useMutation({
    mutationFn: (id) => trades.breakeven(id),
    onSuccess: () => {
      toast.success(`SL moved to entry (${t.entry_price})`);
      qc.invalidateQueries();
      setConfirm(null);
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Breakeven update failed"),
  });

  const busy = closeMut.isPending || beMut.isPending;
  const qtyForPct = (pct) => {
    const q = (t.quantity || 0) * (pct / 100);
    return q < 0.0001 ? q.toExponential(2) : q.toFixed(6);
  };
  const currentSL = t.stop_loss;
  const entry = t.entry_price;
  const beDisabled = currentSL !== null && currentSL !== undefined && (
    (t.side === "long" && currentSL >= entry) ||
    (t.side === "short" && currentSL <= entry)
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        data-testid={`manage-trade-${t.id}`}
        size="sm"
        variant="ghost"
        className="text-slate-700 hover:bg-slate-100 disabled:opacity-40"
        disabled={isClosed}
        onClick={() => { setConfirm(null); setOpen(true); }}
      >
        {isClosed ? "—" : "Manage"}
      </Button>
      <DialogContent className="max-w-md" data-testid={`manage-dialog-${t.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {t.symbol} · <span className={t.side === "long" ? "text-emerald-600" : "text-red-600"}>
              {t.side.toUpperCase()}
            </span>
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Manage this position. All actions execute on {(t.exchange || "the exchange").toUpperCase()} immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-md p-3 border border-slate-200">
          <div><span className="text-slate-500">Entry</span><div className="mono">{entry}</div></div>
          <div><span className="text-slate-500">Qty</span><div className="mono">{t.quantity}</div></div>
          <div><span className="text-slate-500">Leverage</span><div className="mono">{t.leverage}x</div></div>
          <div>
            <span className="text-slate-500">uPnL</span>
            <div className={cn(
              "mono",
              (t.unrealized_pnl || 0) > 0 && "text-emerald-600",
              (t.unrealized_pnl || 0) < 0 && "text-red-600",
            )}>{(t.unrealized_pnl || 0).toFixed(2)} USDT</div>
          </div>
          <div><span className="text-slate-500">SL</span>
            <div className="mono text-red-600">{currentSL ?? "—"}</div></div>
          <div><span className="text-slate-500">TP</span>
            <div className="mono text-emerald-600">{t.take_profit ?? "—"}</div></div>
        </div>

        {confirm === null ? (
          <>
            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">Close</p>
              <div className="grid grid-cols-4 gap-2">
                {[25, 50, 75, 100].map((pct) => (
                  <Button
                    key={pct}
                    data-testid={`close-pct-${pct}-${t.id}`}
                    variant={pct === 100 ? "destructive" : "outline"}
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirm({ kind: "close", percent: pct })}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">Risk</p>
              <Button
                data-testid={`breakeven-${t.id}`}
                variant="outline"
                size="sm"
                className="w-full"
                disabled={busy || beDisabled}
                onClick={() => setConfirm({ kind: "breakeven" })}
                title={beDisabled ? "Current SL is already at or better than breakeven" : undefined}
              >
                Move SL to breakeven ({entry})
              </Button>
              {beDisabled && (
                <p className="text-[11px] text-slate-500">
                  Current SL is already at or better than entry.
                </p>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost" size="sm">Cancel</Button>
              </DialogClose>
            </DialogFooter>
          </>
        ) : (
          <ConfirmActionView
            trade={t}
            confirm={confirm}
            qtyForPct={qtyForPct}
            busy={busy}
            onBack={() => setConfirm(null)}
            onConfirm={() => {
              if (confirm.kind === "close") {
                const pp = confirm.percent === 100 ? null : confirm.percent;
                closeMut.mutate({ id: t.id, partial_percent: pp });
              } else if (confirm.kind === "breakeven") {
                beMut.mutate(t.id);
              }
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmActionView({ trade: t, confirm, qtyForPct, busy, onBack, onConfirm }) {
  const isClose = confirm.kind === "close";
  const isFullClose = isClose && confirm.percent === 100;
  const exchange = (t.exchange || "the exchange").toUpperCase();
  const title = isClose
    ? (isFullClose ? "Close position?" : `Close ${confirm.percent}% of position?`)
    : "Move SL to breakeven?";
  const body = isClose
    ? `Sell ${qtyForPct(confirm.percent)} ${t.symbol} at market. This executes on ${exchange} immediately.`
    : `Update the stop loss to ${t.entry_price} (entry price). If current SL widens risk vs breakeven, this will replace it on ${exchange}.`;
  return (
    <>
      <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-1">
        <p className="font-medium text-amber-900" data-testid="confirm-title">{title}</p>
        <p className="text-sm text-amber-800" data-testid="confirm-body">{body}</p>
      </div>
      <DialogFooter>
        <Button variant="ghost" size="sm" disabled={busy} onClick={onBack} data-testid="confirm-cancel">
          Back
        </Button>
        <Button
          variant={isFullClose ? "destructive" : "default"}
          size="sm"
          disabled={busy}
          onClick={onConfirm}
          data-testid="confirm-execute"
        >
          {busy ? "Executing…" : (isClose ? "Yes, close" : "Yes, move SL")}
        </Button>
      </DialogFooter>
    </>
  );
}

/* ---- Trades tab ---- */

const CLOSED_STATUSES = ["closed_by_signal", "closed_by_tp", "closed_by_sl", "closed_by_auto_roi", "error"];

export default function TradesSection() {
  const q = useQuery({
    queryKey: ["trades", "all"],
    queryFn: async () => (await trades.list()).data,
    refetchInterval: 5000,
  });
  const rows = q.data || [];
  const [statusFilter, setStatusFilter] = useState("all");

  const downloadCsv = () => {
    // Streaming endpoint — hitting it in a new tab preserves auth cookies +
    // avoids buffering a large blob in memory.
    const url = trades.csvUrl();
    window.open(url, "_blank");
  };

  const closed = rows.filter((t) => CLOSED_STATUSES.includes(t.status));
  const open = rows.filter((t) => !CLOSED_STATUSES.includes(t.status) && t.status !== "pending_limit");

  // Win-rate/realized-PnL must match the backend's own /overview definition
  // exactly (server.py's closed_q): real trades only (not paper_mode), not
  // archived, and "error" status excluded (an error close isn't a real win
  // or loss outcome). Reusing the table's broader CLOSED_STATUSES here would
  // silently reintroduce the paper-trade leak already fixed once in Overview
  // — see [[overview_paper_trade_pnl_leak_fix]].
  const closedReal = rows.filter((t) =>
    !t.paper_mode && !t.archived &&
    ["closed_by_signal", "closed_by_tp", "closed_by_sl", "closed_by_auto_roi"].includes(t.status)
  );
  const decided = closedReal.filter((t) => (t.realized_pnl || 0) !== 0);
  const wins = decided.filter((t) => (t.realized_pnl || 0) > 0).length;
  const winRate = decided.length ? (wins / decided.length) * 100 : null;
  const realizedTotal = closedReal.reduce((s, t) => s + (t.realized_pnl || 0), 0);

  const visible = statusFilter === "all" ? rows
    : statusFilter === "open" ? open
    : closed;

  return (
    <div className="space-y-4" data-testid="section-trades">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-surface p-5">
          <p className="overline">Open positions</p>
          <p className="mono text-2xl font-bold mt-2">{open.length}</p>
        </div>
        <div className="card-surface p-5">
          <p className="overline">Closed trades</p>
          <p className="mono text-2xl font-bold mt-2">{closed.length}</p>
        </div>
        <div className="card-surface p-5">
          <p className="overline">Win rate (closed)</p>
          <p className={cn("mono text-2xl font-bold mt-2", winRate != null && (winRate >= 50 ? "text-emerald-600" : "text-red-600"))}>
            {winRate != null ? `${winRate.toFixed(1)}%` : "—"}
          </p>
        </div>
        <div className="card-surface p-5">
          <p className="overline">Realized PnL (real, closed)</p>
          <p className={cn("mono text-2xl font-bold mt-2", realizedTotal > 0 ? "text-emerald-600" : realizedTotal < 0 ? "text-red-600" : "")}>
            {realizedTotal >= 0 ? "+" : "-"}${Math.abs(realizedTotal).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="overline">All trades</p>
            <h3 className="font-display text-xl mt-1">Trade History</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 mr-2">
              {[{ v: "all", label: "All" }, { v: "open", label: "Open" }, { v: "closed", label: "Closed" }].map((s) => (
                <button
                  key={s.v}
                  data-testid={`status-filter-${s.v}`}
                  onClick={() => setStatusFilter(s.v)}
                  className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
                    statusFilter === s.v ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                                          : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <Button
              onClick={downloadCsv}
              data-testid="trades-csv-download"
              variant="outline"
              size="sm"
              className="border-slate-300 hover:bg-slate-50"
            >
              <ListBullets size={16} className="mr-2" />
              Download CSV
            </Button>
          </div>
        </div>
        <PositionsTable
          trades={visible}
          mode="history"
          emptyLabel={statusFilter === "closed" ? "No closed trades yet." : statusFilter === "open" ? "No open positions right now." : "No trades match this filter."}
        />
      </div>
    </div>
  );
}
